"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  SimpleGrid,
  Group,
  Button,
  Accordion,
  Paper,
  Anchor,
  Tooltip,
} from "@mantine/core";
import Link from "next/link";
import { trackCtaClick } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import classes from "./chart.module.css";

// Section counter helper — matches how-to-do-color-analysis/content.tsx SectionHeader
function SectionHeader({
  n,
  label,
  title,
}: {
  n: number;
  label: string;
  title: string;
}) {
  return (
    <Box mb={24}>
      <p className={classes.kicker}>
        N&ordm; {String(n).padStart(2, "0")} &mdash; {label}
      </p>
      <h2 className={classes.sectionTitle}>{title}</h2>
      <hr className={classes.hairline} />
    </Box>
  );
}

// ---------- The master chart: undertone × depth ----------
const undertoneColumns = ["Cool", "Neutral", "Warm"] as const;

const depthRows: {
  label: string;
  cells: Record<(typeof undertoneColumns)[number], { hex: string; season: string }>;
}[] = [
  {
    label: "Fair",
    cells: {
      Cool: { hex: "#F5E0D8", season: "Summer / Winter" },
      Neutral: { hex: "#F2DED0", season: "Light Spring / Light Summer" },
      Warm: { hex: "#F3D8B8", season: "Light Spring" },
    },
  },
  {
    label: "Light",
    cells: {
      Cool: { hex: "#E9C9BC", season: "Light Summer" },
      Neutral: { hex: "#E3BE9E", season: "True Spring / True Summer" },
      Warm: { hex: "#E4B588", season: "True Spring" },
    },
  },
  {
    label: "Medium",
    cells: {
      Cool: { hex: "#CFA08C", season: "True Summer / Cool Winter" },
      Neutral: { hex: "#C99672", season: "Soft Autumn / Soft Summer" },
      Warm: { hex: "#C68A55", season: "True Autumn" },
    },
  },
  {
    label: "Tan",
    cells: {
      Cool: { hex: "#A97C63", season: "Cool Winter" },
      Neutral: { hex: "#A6714A", season: "Soft Autumn" },
      Warm: { hex: "#A6693A", season: "True Autumn / Deep Autumn" },
    },
  },
  {
    label: "Deep",
    cells: {
      Cool: { hex: "#5C3A30", season: "Deep Winter" },
      Neutral: { hex: "#5E3924", season: "Deep Autumn / Deep Winter" },
      Warm: { hex: "#6B4226", season: "Deep Autumn" },
    },
  },
];

// ---------- Best colors by undertone (real colors pulled from constants/colorSeasons) ----------
type Swatch = { name: string; hex: string };

const coolFlattering: Swatch[] = [
  { name: "Electric Blue", hex: "#0055FF" },
  { name: "Hot Pink", hex: "#FF69B4" },
  { name: "Emerald", hex: "#009B77" },
  { name: "Slate Blue", hex: "#5D6D8E" },
  { name: "Dusty Rose", hex: "#D4A5A5" },
  { name: "Ruby", hex: "#E0115F" },
  { name: "Bright Purple", hex: "#6A0DAD" },
  { name: "Mauve", hex: "#C9A0DC" },
];
const coolAvoid: Swatch[] = [
  { name: "Warm Mustard", hex: "#FFDB58" },
  { name: "Pumpkin", hex: "#FF7518" },
  { name: "Warm Camel", hex: "#C19A6B" },
  { name: "Rust Orange", hex: "#B7410E" },
];

const warmFlattering: Swatch[] = [
  { name: "Goldenrod", hex: "#DAA520" },
  { name: "Pumpkin", hex: "#FF7518" },
  { name: "Copper", hex: "#B87333" },
  { name: "Amber", hex: "#FFBF00" },
  { name: "Coral", hex: "#FF6F61" },
  { name: "Warm Olive", hex: "#6B8E23" },
  { name: "Bronze", hex: "#CD7F32" },
  { name: "Rust", hex: "#B7410E" },
];
const warmAvoid: Swatch[] = [
  { name: "Bright Fuchsia", hex: "#D200D2" },
  { name: "Electric Blue", hex: "#0055FF" },
  { name: "Pure Black", hex: "#000000" },
  { name: "Icy Silver", hex: "#C0C0C0" },
];

const neutralFlattering: Swatch[] = [
  { name: "Soft Moss", hex: "#A8B4A5" },
  { name: "Misty Taupe", hex: "#CCC5B9" },
  { name: "Grey Sage", hex: "#8FA79A" },
  { name: "Rosewood", hex: "#B38481" },
  { name: "Warm Moss", hex: "#8A9A5B" },
  { name: "Deep Turquoise", hex: "#317873" },
  { name: "Dusty Teal", hex: "#5E7D7E" },
  { name: "Bronze", hex: "#CD7F32" },
];
const neutralAvoid: Swatch[] = [
  { name: "Bright Red", hex: "#FF0000" },
  { name: "Cool Fuchsia", hex: "#FF00FF" },
  { name: "Jet Black", hex: "#000000" },
  { name: "Bright White", hex: "#FFFFFF" },
];

function SwatchRow({ swatches }: { swatches: Swatch[] }) {
  return (
    <div className={classes.swatchRow}>
      {swatches.map((s) => (
        <Tooltip key={s.name + s.hex} label={s.name} withArrow position="top">
          <div className={classes.swatchItem}>
            <div className={classes.swatchCircle} style={{ background: s.hex }} />
            <span className={classes.swatchName}>{s.name}</span>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

const faqs = [
  {
    question: "How do I find my skin tone on a color chart?",
    answer:
      "Cross your undertone (cool, neutral, or warm) with your depth (how light or dark your skin reads). That intersection is your cell on the master chart above, and it points to the color-season family that usually fits people with that combination. It's a strong starting point, not a final verdict.",
  },
  {
    question: "What colors suit my skin tone?",
    answer:
      "It depends mainly on undertone: cool skin tends to suit blue-based colors and jewel tones, warm skin suits golden and earthy colors, and neutral skin can wear both if it leans slightly toward a muted, balanced palette. See the swatches above for real examples in each group.",
  },
  {
    question: "What's the difference between cool and warm undertone?",
    answer:
      "Cool undertones have pink, red, or blue beneath the skin's surface; warm undertones have peach, gold, or yellow. It's independent of how light or dark you are — a deep skin tone can be warm or cool, same as a fair one. The vein, jewelry, and white-paper tests below help you spot the difference.",
  },
  {
    question: "Can my skin tone change?",
    answer:
      "Your depth changes with tanning or the seasons — you can go from Fair to Medium and back. Your undertone doesn't change; it's determined by your skin's underlying pigment, not by sun exposure. So a tan version of you sits in a deeper row of the same column on the chart.",
  },
  {
    question: "What if I'm neutral undertone?",
    answer:
      "Neutral means your skin doesn't lean clearly warm or cool — you likely got mixed or inconclusive results on the undertone tests. That's common, and it's good news: you can wear colors from both the warm and cool columns, as long as you lean toward the muted, balanced shades in each rather than the most extreme ones.",
  },
  {
    question: "How is this different from a full color analysis?",
    answer:
      "This chart maps skin tone to a general color direction using two variables: undertone and depth. A full 12-season color analysis adds a third variable, chroma (how bright or muted your coloring is), plus your hair and eye color, to narrow you down to one exact palette instead of a family of colors.",
  },
];

export default function SkinToneColorChartContent({ locale }: { locale: string }) {
  void locale;

  let sectionNumber = 0;
  const nextSection = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const linkStyle: React.CSSProperties = {
    color: "inherit",
    textDecoration: "underline",
  };

  return (
    <Container size="xl" py={48}>
      <Stack gap={0}>
        {/* ---------- Hero ---------- */}
        <Box mb={56}>
          <p className={classes.heroKicker}>THE VISUAL REFERENCE</p>
          <h1 className={classes.heroTitle}>Skin Tone Color Chart</h1>
          <p className={classes.heroTagline}>
            Cross your undertone with your depth and you get a color direction &mdash; no
            guesswork. Every swatch below is a real, named color, not a stock photo filter.
          </p>
          <Text fz="sm" c="#6f6a64" mt={20}>
            Want your exact palette instead of a general direction?{" "}
            <Link
              href="/ai-color-analysis"
              style={linkStyle}
              onClick={() => trackCtaClick("stc_early")}
            >
              Get your exact palette from a selfie
            </Link>{" "}
            &mdash; about 90 seconds.
          </Text>
        </Box>

        {/* ---------- N01 The Master Chart ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The Master Chart" title="Undertone × depth" />
          <Text className={classes.lede} mb={24} style={{ fontSize: 17, lineHeight: 1.7, color: "#3a3632" }}>
            Find your undertone along the top, your depth down the side. The cell where they
            meet shows a representative skin-tone swatch and the color-season family most people
            with that combination fall into.
          </Text>
          <Box className={classes.chartScroll}>
            <table className={classes.chartTable}>
              <thead>
                <tr>
                  <th style={{ background: "transparent", border: "none" }} />
                  {undertoneColumns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {depthRows.map((row) => (
                  <tr key={row.label}>
                    <td className={classes.chartRowLabel}>{row.label}</td>
                    {undertoneColumns.map((col) => {
                      const cell = row.cells[col];
                      return (
                        <td key={col} className={classes.chartCell}>
                          <div className={classes.chartSwatch} style={{ background: cell.hex }} />
                          <div className={classes.chartSeasonLabel}>{cell.season}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <Text fz="sm" c="#6f6a64" mt={16} style={{ fontStyle: "italic" }}>
            These are typical mappings, not guarantees &mdash; chroma (how bright vs. muted your
            coloring is) also shifts which exact season fits, which is why some cells span two
            families.
          </Text>
        </Box>

        {/* ---------- N02 Find Yourself ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="Find Yourself" title="Find yourself on the chart" />
          <Text mb={24} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Depth is easy to judge in a mirror. Undertone takes a little more work &mdash; these
            three quick tests will get you most of the way there.
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={20}>
            <Paper withBorder p={20} radius="md">
              <Text fw={700} mb={8} className={classes.serif} fz="lg">
                The vein test
              </Text>
              <Text fz="sm" c="#3a3632" style={{ lineHeight: 1.6 }}>
                Look at the veins on your inner wrist in daylight. Blue or purple suggests cool;
                green or olive suggests warm; a genuine mix suggests neutral.
              </Text>
            </Paper>
            <Paper withBorder p={20} radius="md">
              <Text fw={700} mb={8} className={classes.serif} fz="lg">
                The jewelry test
              </Text>
              <Text fz="sm" c="#3a3632" style={{ lineHeight: 1.6 }}>
                Lay gold and silver against bare skin. One sits &quot;in&quot; your skin, the
                other sits &quot;on&quot; it like a costume. Gold flatters warm, silver flatters
                cool.
              </Text>
            </Paper>
            <Paper withBorder p={20} radius="md">
              <Text fw={700} mb={8} className={classes.serif} fz="lg">
                The white-paper test
              </Text>
              <Text fz="sm" c="#3a3632" style={{ lineHeight: 1.6 }}>
                Hold pure white paper under your chin, then cream paper. Warm undertones look
                better against cream; cool undertones look better against stark white.
              </Text>
            </Paper>
          </SimpleGrid>
          <Text mt={24} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Want a guided version of these tests? Try the{" "}
            <Link href="/undertone-quiz" style={linkStyle}>
              undertone quiz
            </Link>{" "}
            or the{" "}
            <Link href="/skin-tone-test" style={linkStyle}>
              skin tone test
            </Link>
            .
          </Text>
        </Box>

        {/* ---------- N03 Best Colors by Undertone ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="The Palettes" title="Best colors by undertone" />

          <Box mb={40}>
            <Text fw={700} mb={4} className={classes.serif} fz="xl">
              Cool undertone
            </Text>
            <Text fz="sm" c="#6f6a64" mb={16}>
              Blue-based, jewel-toned, and icy colors read best against pink or blue undertones.
            </Text>
            <SwatchRow swatches={coolFlattering} />
            <Text fz="xs" c="#a8a29b" tt="uppercase" fw={600} mt={20} mb={10} style={{ letterSpacing: "0.08em" }}>
              Tends to wash out
            </Text>
            <SwatchRow swatches={coolAvoid} />
          </Box>

          <Box mb={40}>
            <Text fw={700} mb={4} className={classes.serif} fz="xl">
              Warm undertone
            </Text>
            <Text fz="sm" c="#6f6a64" mb={16}>
              Golden, peachy, and earthy colors read best against yellow or golden undertones.
            </Text>
            <SwatchRow swatches={warmFlattering} />
            <Text fz="xs" c="#a8a29b" tt="uppercase" fw={600} mt={20} mb={10} style={{ letterSpacing: "0.08em" }}>
              Tends to wash out
            </Text>
            <SwatchRow swatches={warmAvoid} />
          </Box>

          <Box mb={16}>
            <Text fw={700} mb={4} className={classes.serif} fz="xl">
              Neutral undertone
            </Text>
            <Text fz="sm" c="#6f6a64" mb={16}>
              Muted, balanced colors &mdash; neither icy nor golden &mdash; work best when your
              undertone doesn&apos;t clearly lean either way.
            </Text>
            <SwatchRow swatches={neutralFlattering} />
            <Text fz="xs" c="#a8a29b" tt="uppercase" fw={600} mt={20} mb={10} style={{ letterSpacing: "0.08em" }}>
              Tends to wash out
            </Text>
            <SwatchRow swatches={neutralAvoid} />
          </Box>
        </Box>

        {/* ---------- Mid-page CTA (slim) ---------- */}
        <Box mb={56} className={`${classes.ctaBand} ${classes.ctaBandSlim}`}>
          <Text className={classes.ctaHeadline} fz={{ base: 18, md: 20 }}>
            These charts get you close. A photo-based analysis reads your actual undertone, depth
            and chroma and gives you one exact palette.
          </Text>
          <Group gap="sm" wrap="wrap" mt="md">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("stc_mid")}
              size="md"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get my exact palette
            </Button>
          </Group>
        </Box>

        {/* ---------- N04 Skin Tone vs Undertone vs Season ---------- */}
        <Box mb={56}>
          <SectionHeader
            n={nextSection()}
            label="The Distinction"
            title="Skin tone vs. undertone vs. color season"
          />
          <Text mb={16} style={{ lineHeight: 1.7, color: "#3a3632" }}>
            These three terms get used interchangeably, but they measure different things.{" "}
            <Text fw={700} span>Skin tone</Text> is how light or dark your skin is.{" "}
            <Text fw={700} span>Undertone</Text> is the underlying hue &mdash; cool, warm, or
            neutral &mdash; independent of depth. Your{" "}
            <Text fw={700} span>color season</Text> is the full picture: undertone, depth,{" "}
            <em>and</em> chroma (how bright or muted your coloring reads), plus your hair and eye
            color. A chart like this one can only use the first two, which is why it points to a
            family of seasons rather than one exact answer.
          </Text>
          <Text style={{ lineHeight: 1.7, color: "#3a3632" }}>
            Go deeper on{" "}
            <Link href="/best-colors-for-skin-tone" style={linkStyle}>
              best colors for skin tone
            </Link>
            , see every palette in the{" "}
            <Link href="/color-seasons-chart" style={linkStyle}>
              12 season color chart
            </Link>
            , read the full breakdown of{" "}
            <Link href="/seasonal-color-analysis" style={linkStyle}>
              seasonal color analysis
            </Link>
            , or jump straight to the{" "}
            <Link href="/color-analysis-quiz" style={linkStyle}>
              color analysis quiz
            </Link>
            .
          </Text>
        </Box>

        {/* ---------- N05 FAQ ---------- */}
        <Box mb={56}>
          <SectionHeader n={nextSection()} label="FAQ" title="Questions people actually ask" />
          <Accordion variant="separated">
            {faqs.map((faq, i) => (
              <Accordion.Item key={i} value={`faq-${i}`}>
                <Accordion.Control>
                  <Text fw={500} className={classes.serif} fz="lg">
                    {faq.question}
                  </Text>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="#6f6a64">
                    {faq.question === "How is this different from a full color analysis?" ? (
                      <>
                        This chart maps skin tone to a general color direction using two
                        variables: undertone and depth. A full{" "}
                        <Anchor component={Link} href="/seasonal-color-analysis" fz="sm" c="#6f6a64" td="underline">
                          12-season color analysis
                        </Anchor>{" "}
                        adds a third variable, chroma, plus hair and eye color, to narrow you down
                        to one exact palette instead of a family of colors.
                      </>
                    ) : (
                      faq.answer
                    )}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* ---------- Bottom CTA ---------- */}
        <Paper className={classes.ctaBand} ta="center" mb={40}>
          <Text
            tt="uppercase"
            fz={11}
            fw={600}
            c="rgba(255,255,255,0.6)"
            style={{ letterSpacing: "0.14em" }}
            mb={8}
          >
            FROM CHART TO EXACT PALETTE
          </Text>
          <Title order={2} className={classes.ctaHeadline} fz={{ base: 26, md: 34 }}>
            Skip the guesswork
          </Title>
          <Text className={classes.ctaSubtitle} fz="md" maw={560} mx="auto">
            This chart gets you a family of colors. Our AI reads your actual undertone, depth and
            chroma from two photos and gives you your exact 12-season palette &mdash; and the
            colors to avoid &mdash; in about 90 seconds.
          </Text>
          <Group justify="center" gap="md" wrap="wrap">
            <Button
              component={Link}
              href="/ai-color-analysis"
              onClick={() => trackCtaClick("stc_bottom")}
              size="lg"
              radius={999}
              fw={700}
              styles={{ root: { background: "white", color: "#141414" } }}
            >
              Get your color analysis
            </Button>
          </Group>
          <Text fz="sm" mt={16} style={{ color: "rgba(255,255,255,0.7)" }}>
            Prefer a quiz? Start with the{" "}
            <Link href="/color-analysis-quiz" style={{ color: "inherit", textDecoration: "underline" }}>
              free color analysis quiz
            </Link>
            .
          </Text>
        </Paper>

        {/* ---------- Email capture ---------- */}
        <Box mb={40}>
          <EmailResultsCapture
            source="skin-tone-color-chart"
            title="Want the printable version?"
            subtitle="Get this chart plus a season-by-season palette cheat sheet in your inbox."
          />
        </Box>

        {/* ---------- Keep Reading ---------- */}
        <Box>
          <p className={classes.kicker}>KEEP READING</p>
          <h3 className={classes.sectionTitle} style={{ fontSize: 22 }}>Go deeper</h3>
          <hr className={classes.hairline} style={{ marginBottom: 20 }} />
          <SimpleGrid cols={{ base: 1, sm: 3, md: 5 }} spacing={12}>
            {[
              { href: "/color-seasons-chart", label: "The 12 seasons, charted" },
              { href: "/undertone-quiz", label: "Undertone quiz" },
              { href: "/skin-tone-test", label: "Skin tone test" },
              { href: "/best-colors-for-skin-tone", label: "Best colors for skin tone" },
              { href: "/how-to-do-color-analysis", label: "How to do color analysis at home" },
            ].map((l) => (
              <Button
                key={l.href}
                component={Link}
                href={l.href}
                variant="default"
                size="sm"
                styles={{
                  root: { height: "auto", minHeight: 36, padding: "8px 12px" },
                  label: { whiteSpace: "normal", textAlign: "center", lineHeight: 1.3 },
                }}
              >
                {l.label}
              </Button>
            ))}
          </SimpleGrid>
        </Box>
      </Stack>
    </Container>
  );
}
